import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { assetUrl } from '../utils/assetUrl';
import { normalizeTags } from '../utils/tags';
import { TagList } from './TagList';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(44, 40, 37, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.space[5]}px;
  z-index: 100;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Panel = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.lg}px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 24px 64px ${theme.colors.shadow};
  animation: slideUp 0.25s ease;

  @keyframes slideUp {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ImageFrame = styled.div`
  width: 100%;
  background: ${theme.colors.bgAlt};
  padding: ${theme.space[6]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.border};
`;

const ImageButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: zoom-in;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: 2px;
  }
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 50vh;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  display: block;
`;

const Body = styled.div`
  padding: ${theme.space[5]}px ${theme.space[6]}px ${theme.space[6]}px;
`;

const PieceTitle = styled.h2`
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.xl};
  font-weight: normal;
  margin: 0 0 ${theme.space[2]}px;
`;

const DateText = styled.time`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.muted};
  margin-bottom: ${theme.space[4]}px;
`;

const Description = styled.p`
  margin: 0 0 ${theme.space[4]}px;
  color: ${theme.colors.text};
  line-height: 1.6;
  white-space: pre-line;
`;

const TagsWrap = styled.div`
  margin-top: ${theme.space[2]}px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: ${theme.space[4]}px;
  right: ${theme.space[4]}px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.pill}px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  color: ${theme.colors.muted};
  z-index: 1;

  &:hover {
    color: ${theme.colors.text};
    border-color: ${theme.colors.text};
  }
`;

const ModalWrap = styled.div`
  position: relative;
  max-width: 720px;
  width: 100%;
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: ${theme.colors.gallery};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  animation: fadeIn 0.15s ease;
`;

const FullscreenImg = styled.img`
  width: 100vw;
  height: 100vh;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  pointer-events: none;
`;

const FullscreenHint = styled.span`
  position: fixed;
  bottom: ${theme.space[4]}px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: ${theme.fontSizes.xs};
  pointer-events: none;
`;

function formatMonthYear(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export function ArtModal({ piece, onClose }) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  useEffect(() => {
    setIsFullscreen(false);
  }, [piece?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isFullscreen) closeFullscreen();
        else onClose();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, isFullscreen, closeFullscreen]);

  if (!piece) return null;

  const tags = normalizeTags(piece.tags);
  const imageSrc = assetUrl(piece.imageUrl);

  return (
    <>
      <Backdrop onClick={onClose} role="presentation">
        <ModalWrap onClick={(e) => e.stopPropagation()}>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseBtn>
          <Panel role="dialog" aria-modal="true" aria-labelledby="piece-title">
            <ImageFrame>
              <ImageButton
                type="button"
                onClick={() => setIsFullscreen(true)}
                aria-label={`View ${piece.title} fullscreen`}
              >
                <Img src={imageSrc} alt={piece.title} />
              </ImageButton>
            </ImageFrame>
            <Body>
              <PieceTitle id="piece-title">{piece.title}</PieceTitle>
              <DateText dateTime={piece.date}>{formatMonthYear(piece.date)}</DateText>
              {piece.description && (
                <Description>{piece.description}</Description>
              )}
              {tags.length > 0 && (
                <TagsWrap>
                  <TagList tags={tags} />
                </TagsWrap>
              )}
            </Body>
          </Panel>
        </ModalWrap>
      </Backdrop>

      {isFullscreen && (
        <FullscreenOverlay
          onClick={closeFullscreen}
          role="dialog"
          aria-modal="true"
          aria-label={`${piece.title} fullscreen`}
        >
          <FullscreenImg src={imageSrc} alt={piece.title} />
          <FullscreenHint>Click or press Escape to exit</FullscreenHint>
        </FullscreenOverlay>
      )}
    </>
  );
}
